import { z } from 'zod'

export const CATEGORIAS_OBLIGACION = [
  'laboral',
  'contable',
  'societaria',
  'impositiva',
  'previsional',
  'otra',
] as const

export type CategoriaObligacion = (typeof CATEGORIAS_OBLIGACION)[number]

export const CATEGORIA_OBLIGACION_LABELS: Record<CategoriaObligacion, string> = {
  laboral: 'Laboral',
  contable: 'Contable',
  societaria: 'Societaria',
  impositiva: 'Impositiva',
  previsional: 'Previsional',
  otra: 'Otra',
}

export const CATEGORIAS_OBLIGACION_OPTIONS = CATEGORIAS_OBLIGACION.map((cat) => ({
  value: cat,
  label: CATEGORIA_OBLIGACION_LABELS[cat],
}))

export const PERIODICIDADES_OBLIGACION = [
  'mensual',
  'bimestral',
  'trimestral',
  'cuatrimestral',
  'semestral',
  'anual',
] as const

export type PeriodicidadObligacion = (typeof PERIODICIDADES_OBLIGACION)[number]

export const PERIODICIDAD_OBLIGACION_LABELS: Record<PeriodicidadObligacion, string> = {
  mensual: 'Mensual',
  bimestral: 'Bimestral',
  trimestral: 'Trimestral',
  cuatrimestral: 'Cuatrimestral',
  semestral: 'Semestral',
  anual: 'Anual',
}

export const PERIODICIDADES_OBLIGACION_OPTIONS = PERIODICIDADES_OBLIGACION.map((p) => ({
  value: p,
  label: PERIODICIDAD_OBLIGACION_LABELS[p],
}))

export const COLORES_OBLIGACION = [
  'gray',
  'red',
  'orange',
  'amber',
  'yellow',
  'green',
  'emerald',
  'teal',
  'cyan',
  'blue',
  'sky',
  'indigo',
  'violet',
  'purple',
] as const

export type ColorObligacion = (typeof COLORES_OBLIGACION)[number]

export const COLOR_OBLIGACION_LABELS: Record<ColorObligacion, string> = {
  gray: 'Gris',
  red: 'Rojo',
  orange: 'Naranja',
  amber: 'Ambar',
  yellow: 'Amarillo',
  green: 'Verde',
  emerald: 'Esmeralda',
  teal: 'Teal',
  cyan: 'Cian',
  blue: 'Azul',
  sky: 'Celeste',
  indigo: 'Indigo',
  violet: 'Violeta',
  purple: 'Púrpura',
}

export const MODOS_ASIGNACION = ['todos', 'filtros', 'clientes'] as const

export type ModoAsignacion = (typeof MODOS_ASIGNACION)[number]

export const MODO_ASIGNACION_LABELS: Record<ModoAsignacion, string> = {
  todos: 'Todos los clientes',
  filtros: 'Por filtros (condición IVA / impuesto)',
  clientes: 'Clientes específicos',
}

export const MODO_ASIGNACION_DESCRIPCIONES: Record<ModoAsignacion, string> = {
  todos: 'Genera vencimientos para todos los clientes activos',
  filtros: 'Solo clientes que cumplan la condición IVA y/o tengan el impuesto asignado',
  clientes: 'Solo los clientes que elijas manualmente',
}

export const obligacionRecurrenteSchema = z.object({
  nombre: z
    .string()
    .min(1, 'El nombre es requerido')
    .max(150, 'El nombre no puede exceder 150 caracteres'),

  descripcion: z
    .string()
    .max(500, 'La descripción no puede exceder 500 caracteres')
    .nullable()
    .optional()
    .transform((val) => val?.trim() || null),

  categoria: z.enum(CATEGORIAS_OBLIGACION, {
    error: 'Selecciona una categoría',
  }),

  periodicidad: z.enum(PERIODICIDADES_OBLIGACION, {
    error: 'Selecciona una periodicidad',
  }),

  dia_vencimiento: z
    .number()
    .int('Debe ser un número entero')
    .min(1, 'Mínimo día 1')
    .max(31, 'Máximo día 31'),

  mes_offset: z
    .number()
    .int('Debe ser un número entero')
    .min(0, 'Mínimo 0')
    .max(12, 'Máximo 12'),

  aplica_dia_habil: z.boolean().default(false),

  modo_asignacion: z.enum(MODOS_ASIGNACION).default('todos'),

  condicion_iva_filtro: z
    .array(z.string())
    .nullable()
    .optional()
    .default(null),

  impuesto_filtro: z
    .array(z.string())
    .nullable()
    .optional()
    .default(null),

  clientes_filtro: z
    .array(z.string())
    .nullable()
    .optional()
    .default(null),

  color: z.enum(COLORES_OBLIGACION).nullable().optional().default('blue'),

  activo: z.boolean().default(true),
}).superRefine((data, ctx) => {
  if (data.modo_asignacion === 'filtros') {
    const tieneIva = (data.condicion_iva_filtro?.length ?? 0) > 0
    const tieneImpuesto = (data.impuesto_filtro?.length ?? 0) > 0
    if (!tieneIva && !tieneImpuesto) {
      ctx.addIssue({
        code: 'custom',
        message: 'Seleccioná al menos un filtro (condición IVA o impuesto)',
        path: ['condicion_iva_filtro'],
      })
    }
  }
  if (data.modo_asignacion === 'clientes' && (data.clientes_filtro?.length ?? 0) === 0) {
    ctx.addIssue({
      code: 'custom',
      message: 'Seleccioná al menos un cliente',
      path: ['clientes_filtro'],
    })
  }
})

export type ObligacionRecurrenteFormValues = z.infer<typeof obligacionRecurrenteSchema>

export const DEFAULT_OBLIGACION_VALUES: ObligacionRecurrenteFormValues = {
  nombre: '',
  descripcion: null,
  categoria: 'impositiva',
  periodicidad: 'mensual',
  dia_vencimiento: 15,
  mes_offset: 1,
  aplica_dia_habil: false,
  modo_asignacion: 'todos',
  condicion_iva_filtro: null,
  impuesto_filtro: null,
  clientes_filtro: null,
  color: 'blue',
  activo: true,
}
